import React, { useEffect, useRef, useState } from 'react';
import { C } from '../portalkit';
import { money } from './plannerModel';
import { SKIPS, normalizeSkip } from './plannerSkips';

export { money };
export const fmtLocal = (local) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(local || '');
  if (!m) return local || '';
  const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]));
  return `${d.toLocaleDateString('en-US', { weekday: 'short', timeZone: 'UTC' })} ${d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })} ${d.getUTCDate()} · ${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`.toUpperCase();
};
const fmtDate = (ymd) => { const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd || ''); if (!m) return ymd || ''; const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])); return `${d.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' })} ${d.getUTCDate()}`.toUpperCase(); };

const WHY = Object.fromEntries(SKIPS.map((s) => [s.key, s.why]));

/**
 * One plan card (design_handoff_auth_profile_planner §5 "Cards"). Radius 24, padding 18, min 150.
 *   Waiting: #FAFAFA with two skeleton bars; QUEUED, or WORKING… on the current beat (2px inset).
 *   Filled: white; the headline rises in; rows on #F5F5F5 with an optional green dot; a single
 *   cyan scan line runs top to bottom once as it fills (never replays).
 *   Skipped: a SKIPPED pill, the reason, and + PLAN IT ANYWAY.
 * `rows` = [{ k, v, ok }].
 */
export function PlanCard({ label, waiting = false, active = false, skipped = false, skipWhy, onUnskip, title, rows = [], img, children, pending, testId }) {
  const [scan, setScan] = useState(false);
  const wasWaiting = useRef(waiting);
  useEffect(() => {
    if (wasWaiting.current && !waiting && !skipped) { setScan(true); const t = setTimeout(() => setScan(false), 600); return () => clearTimeout(t); }
    wasWaiting.current = waiting;
    return undefined;
  }, [waiting, skipped]);
  const filled = !waiting && !skipped;
  return (
    <div
      data-testid={testId || `card-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}
      data-state={skipped ? 'skipped' : waiting ? 'waiting' : 'filled'}
      style={{ position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 12, padding: 18, borderRadius: 24, background: filled || skipped ? '#fff' : C.waiting, boxShadow: active && waiting ? 'inset 0 0 0 2px #000' : 'none', minHeight: 150, boxSizing: 'border-box', transition: 'background-color .3s' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.16em', color: C.gray500, textTransform: 'uppercase' }}>{label}</span>
        {skipped && <span style={{ height: 20, padding: '0 8px', display: 'flex', alignItems: 'center', borderRadius: 999, background: C.skeleton, color: C.gray500, fontSize: 9, fontWeight: 700, letterSpacing: '.14em' }}>SKIPPED</span>}
        {waiting && !skipped && <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: C.gray400 }}>{active ? 'WORKING…' : 'QUEUED'}</span>}
      </div>
      {filled && (
        <>
          {scan && <div className="pl-scan" data-motion="1" />}
          {title && <span className="pk-rise" style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.4, textWrap: 'pretty' }}>{title}</span>}
          {rows.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {rows.map((r) => (
                <div key={r.k} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '9px 12px', borderRadius: 12, background: C.inside }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, fontSize: 10, fontWeight: 600, letterSpacing: '.1em', lineHeight: 1.45, color: C.gray600, textTransform: 'uppercase' }}>
                    {r.ok && <span aria-hidden="true" style={{ flexShrink: 0, width: 8, height: 8, borderRadius: '50%', background: C.green }} />}
                    {r.dot && !r.ok && <span aria-hidden="true" style={{ flexShrink: 0, width: 8, height: 8, borderRadius: '50%', background: r.dot }} />}
                    {r.k}
                  </span>
                  <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 700 }}>{r.v}</span>
                </div>
              ))}
            </div>
          )}
          {img && <img src={img} alt="" data-testid={`${testId || label.toLowerCase().replace(/[^a-z]+/g, '-')}-poster`} style={{ width: '100%', height: 120, objectFit: 'cover', borderRadius: 14, display: 'block' }} />}
          {pending && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: '14px 10px', borderRadius: 14, background: C.waiting, boxShadow: `inset 0 0 0 1px ${C.skeleton}` }}>
              <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.14em', color: C.gray400 }}>{pending}</span>
            </div>
          )}
          {children}
        </>
      )}
      {skipped && (
        <>
          <span style={{ fontSize: 13, lineHeight: 1.5, color: C.gray500, textWrap: 'pretty' }}>{skipWhy || 'You told the planner to leave this out.'}</span>
          {onUnskip && <button type="button" onClick={onUnskip} className="pk pk-btn pk-outline-on-white" style={{ alignSelf: 'flex-start', marginTop: 'auto', height: 32, padding: '0 14px', fontSize: 10, fontWeight: 700, letterSpacing: '.14em' }}>+ PLAN IT ANYWAY</button>}
        </>
      )}
      {waiting && !skipped && (
        <div aria-hidden="true" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <span style={{ height: 12, width: '70%', borderRadius: 999, background: C.skeleton }} />
          <span style={{ height: 12, width: '45%', borderRadius: 999, background: C.skeleton }} />
        </div>
      )}
    </div>
  );
}

/** A pick list under a card: tap a candidate to choose it. Chosen = black pill. */
export function Chooser({ options, chosenId, onChoose, disabled, label }) {
  if (!options || options.length < 2) return null;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.14em', color: C.gray400, textTransform: 'uppercase' }}>{label}</span>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {options.map((o) => {
          const on = o.id === chosenId;
          return (
            <button key={o.id} type="button" disabled={disabled} onClick={() => onChoose(o.id)} aria-pressed={on} className={`pk pk-btn ${on ? 'pk-primary-on-white' : 'pk-secondary-on-white'}`} style={{ height: 32, padding: '0 12px', fontSize: 10, fontWeight: 700, letterSpacing: '.08em', textTransform: 'none', maxWidth: '100%' }}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.tag ? `${o.name} · ${o.tag}` : o.name}</span>
              {o.right && <span style={{ flexShrink: 0 }}>{o.right}</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const basisTag = (b) => ({ pro_listing: 'PRO LISTING', web_verified: 'VERIFIED', llm_estimate: 'ESTIMATE', own: 'YOURS' }[b] || '');

/**
 * All seven cards from the plan document. Each fills when the beat it waits on has passed on
 * screen (`step`), or at once when the plan is ready — SELL IT included (`ready || step > needs`).
 * A skipped card says SKIPPED with its reason and offers + PLAN IT ANYWAY.
 */
export default function PlanCards({ plan, doc, step = 1, ready = false, skip, onChoose, onPermit, onUnskip, busy, posterOnDraft }) {
  const st = doc?.stages || {};
  const sk = normalizeSkip(skip ?? doc?.skip);
  const intent = doc?.intent || {};
  const venue = (doc?.venues || []).find((v) => v.id === doc?.chosenVenueId);
  const sound = (doc?.sound || []).find((s) => s.businessId === doc?.chosenSoundId);
  const staff = doc?.staff || [];
  const bar = staff.find((s) => s.role === 'bartender')?.count || 0;
  const serve = staff.find((s) => s.role === 'servers')?.count || 0;
  const sec = staff.find((s) => s.role === 'security')?.count || 0;
  const crew = staff.reduce((n, s) => n + (s.count || 0), 0);
  const staffCost = staff.reduce((n, s) => n + (s.costCents || 0), 0);
  const fin = doc?.money || {};
  const required = (doc?.permits || []).filter((p) => p.status === 'required');
  const soonest = required.map((p) => p.dueLocal).filter(Boolean).sort()[0];

  // A card is filled once its beat has passed on screen AND the server has its data; the stage
  // is paced, so both have to be true or a headline would flash in before its scene played.
  const filled = (needs, flag) => ready || (step > needs && (flag == null || flag));
  const common = (needs, flag, key) => ({ waiting: !filled(needs, flag), active: !ready && step === needs, skipped: !!(key && sk[key]), skipWhy: key ? WHY[key] : undefined, onUnskip: key && onUnskip ? () => onUnskip(key) : undefined });

  return (
    <>
      <PlanCard label="THE EVENT" {...common(1, st.prompt)} title={doc?.title || intent.title}
        rows={[{ k: 'WHEN', v: fmtLocal(intent.startLocal) }, { k: 'GUESTS', v: `~${intent.headcount || 0}` }]} />

      <PlanCard label="SPACE" {...common(3, st.verify)} title={venue?.name}
        rows={venue ? [
          { k: venue.basis === 'own' ? 'YOUR VENUE' : 'PRIVATE HIRE', v: basisTag(venue.basis), ok: venue.basis !== 'llm_estimate', dot: venue.basis === 'llm_estimate' ? '#EAB308' : undefined },
          { k: 'CAP · COST', v: `${venue.capacity || '—'} · ${money(venue.costCents)}` },
        ] : []}>
        <Chooser label="OTHER SPACES" chosenId={doc?.chosenVenueId} disabled={busy} onChoose={(id) => onChoose({ kind: 'venue', venueId: id })}
          options={(doc?.venues || []).map((v) => ({ id: v.id, name: v.name, tag: basisTag(v.basis), right: money(v.costCents) }))} />
      </PlanCard>

      <PlanCard label="SOUND" {...common(4, st.sound, 'sound')} title={intent.wantsDj ? (sound?.name || 'DJ to be booked') : 'No DJ'}
        rows={intent.wantsDj ? [{ k: 'SET', v: `${(intent.music || 'MUSIC').toUpperCase()} · ${intent.durationHours || 0} HRS` }, { k: 'COST', v: money(doc?.soundCostCents) }] : [{ k: 'HOUSE SYSTEM', v: 'INCLUDED', ok: true }]}>
        <Chooser label="OTHER DJS" chosenId={doc?.chosenSoundId} disabled={busy} onChoose={(id) => onChoose({ kind: 'sound', businessId: id })}
          options={(doc?.sound || []).map((s) => ({ id: s.businessId, name: s.name, tag: s.isListed ? 'LISTED' : s.available === false ? 'BUSY' : s.available ? 'FREE' : null, right: s.priceHint || '' }))} />
      </PlanCard>

      <PlanCard label="STAFF" {...common(5, st.staff, 'staff')} title={crew > 0 ? `${crew} crew, ${doc?.staffHours || 0} hrs incl. setup` : 'No crew needed'}
        rows={crew > 0 ? [{ k: 'BAR · SERVE · SEC', v: `${bar} · ${serve} · ${sec}` }, { k: 'COST', v: money(staffCost) }] : []} />

      <PlanCard label="PERMITS & MUST-DOS" {...common(6, st.permits, 'permits')}
        title={required.length ? `${required.length} filing${required.length === 1 ? '' : 's'}${soonest ? `, due ${fmtDate(soonest)}` : ''}` : 'Nothing to file'}
        rows={(doc?.permits || []).slice(0, 4).map((p) => ({
          k: p.name, v: p.status === 'required' ? 'REQUIRED' : p.status === 'done' ? 'DONE' : p.status === 'in_place' ? 'IN PLACE' : 'N/A',
          ok: p.status === 'done' || p.status === 'in_place', dot: p.status === 'required' ? '#EAB308' : p.status === 'not_needed' ? C.gray400 : undefined,
        }))}>
        {onPermit && required.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {required.slice(0, 4).map((p) => (
              <button key={p.name} type="button" disabled={busy} onClick={() => onPermit(p.name, 'done')} className="pk pk-btn pk-outline-on-white" style={{ height: 32, padding: '0 12px', fontSize: 9, fontWeight: 700, letterSpacing: '.12em' }}>
                {p.name.toUpperCase()} · DONE
              </button>
            ))}
          </div>
        )}
      </PlanCard>

      <PlanCard label="THE MONEY" {...common(7, st.numbers, 'numbers')} title={`Recommended ${money(fin.recommendedTicketCents)}`}
        rows={[{ k: 'BREAK-EVEN', v: `${fin.breakEvenGuests || 0} GUESTS` }, { k: `AT ${intent.headcount || 0} SOLD`, v: `${(fin.marginAtCapacityCents || 0) >= 0 ? '+' : '−'}${money(Math.abs(fin.marginAtCapacityCents || 0))}` }]} />

      <PlanCard label="SELL IT" {...common(8, st.numbers)} skipped={false} title={doc?.title ? `${doc.title} · ${fmtDate(intent.startLocal)}` : undefined}
        rows={[{ k: 'TICKET', v: sk.numbers ? 'FREE' : money(fin.recommendedTicketCents) }, { k: 'ON SALE', v: fmtDate(doc?.onSaleLocal) }]}
        img={!sk.poster && plan?.posterUrl ? plan.posterUrl : undefined}
        pending={plan?.posterUrl || sk.poster ? null : posterOnDraft ? 'POSTER · DRAWING…' : 'POSTER · GENERATED WHEN YOU APPROVE'} />
    </>
  );
}
